import { z } from "zod";
import {
  Box,
  Text,
  WidgetInteractive,
  WidgetSkeleton,
  compactNumber,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { resolveStoredTitle } from "../shared/derived-titles";
import { useMetric, type MetricMeasure } from "./use-metric";

/**
 * `metric` — a single big-number calculation card (a "stat"): the count of
 * projects in scope, or the count with no assignee. Purely presentational chrome
 * over the shared count reader (`useMetric` folds scope + the card's Filters blob
 * the same way `useDimensionSlices` does). Serves the Custom "Calculation / Metric"
 * and the assignee "Total unassigned" presets. The card's own title (rename) names
 * it; the muted caption says what the number counts.
 */
export const metricConfigSchema = z
  .looseObject({
    measure: z.enum(["total", "unassigned"]).default("total"),
    compact: z.boolean().default(false),
    dateField: z.enum(["creationDate", "lastUpdateDate"]).optional(),
    filters: widgetFiltersSchema.nullish(),
  });

export type MetricConfig = z.infer<typeof metricConfigSchema>;

/** Per-measure caption + empty copy — keyed by the same enum the reader takes. */
const MEASURE_META: Record<MetricMeasure, { caption: string }> = {
  total: { caption: "projects" },
  unassigned: { caption: "unassigned projects" },
};

/** Title mechanics: the per-measure derived titles plus the generic gallery
 *  names are metric-generated and re-derive when the measure changes; curated
 *  seed titles ("New this week", "Active projects") and renames stick. */
const MEASURE_TITLE: Record<MetricMeasure, string> = {
  total: "Total projects",
  unassigned: "Total unassigned",
};
const GENERATED_METRIC_TITLES = new Set(["Metric", "Calculation", ...Object.values(MEASURE_TITLE)]);

function MetricRender({ h, config }: WidgetRenderContext<MetricConfig>) {
  // Older persisted Team Overview cards predate `dateField`; keep those cards on
  // the activity-oriented update window without forcing a layout migration.
  const dateField =
    config.dateField === "lastUpdateDate" || config.widgetKey === "team-overview.active"
      ? "lastUpdateDate"
      : "creationDate";
  const { value, loading, error } = useMetric(
    config.measure,
    config.filters,
    dateField,
  );
  // Click-through: the number opens this card's Data drawer. The card has no
  // discrete elements to drill into, so it opens the full window — narrowed to
  // the unassigned set when that's what it counts.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="stat" />;
  }

  const { caption } = MEASURE_META[config.measure];
  const canDrill = Boolean(drill && instance);
  const openData = () => {
    if (!drill || !instance) return;
    if (config.measure === "unassigned") {
      drill.openData(instance.instanceId, { kind: "unassigned", label: "Unassigned" });
    } else {
      drill.openData(instance.instanceId, null);
    }
  };

  // The card is `bodyDraggable`: its padding ring plus header remain the grab
  // surface, while `WidgetInteractive` fills the body. Pressing the body drills
  // into the data instead of grabbing the card, and hover marks it as clickable.
  return (
    <Box fill="flex" align="center" justify="center" paddingY="xs">
      <WidgetInteractive
        fill="cell"
        rounded="lg"
        align="center"
        justify="center"
        cursor={canDrill ? "pointer" : undefined}
        hoverBackground={canDrill}
        onClick={canDrill ? openData : undefined}
      >
        <Box align="center" gap="2xs">
          <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {config.compact ? compactNumber(value) : value.toLocaleString()}
          </Text>
          <Text size="sm" variant="muted" truncate={1}>
            {caption}
          </Text>
        </Box>
      </WidgetInteractive>
    </Box>
  );
}

const metricSettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: [
      {
        key: "measure",
        kind: "select",
        label: "Measure",
        icon: "hash",
        options: [
          { value: "total", label: "Total projects" },
          { value: "unassigned", label: "Unassigned projects" },
        ],
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "compact", kind: "toggle", label: "Abbreviate large numbers", icon: "minimize-2" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const metricDef: WidgetModule<MetricConfig> = {
  type: "metric",
  title: "Metric",
  dynamicTitle: (config) =>
    resolveStoredTitle(
      config.title,
      (title) => GENERATED_METRIC_TITLES.has(title),
      MEASURE_TITLE[config.measure],
    ),
  icon: "hash",
  span: { minW: 2, minH: 2, maxW: 6, maxH: 4, defaultW: 3, defaultH: 2 },
  configSchema: metricConfigSchema,
  settings: metricSettings,
  headerActions: WidgetHeaderFilters,
  // A near-empty card: grab it from anywhere; its one number is carved out as
  // the clickable drilldown block (see MetricRender).
  bodyDraggable: true,
  catalog: [
    {
      key: "metric.custom",
      label: "Calculation",
      description: "A single big number — the count of projects in scope.",
      section: "Custom",
      // No baked title: the label is in the generated family, so the module's
      // `dynamicTitle` names the card from `measure` and tracks changes.
      autoTitle: false,
      defaultConfig: { measure: "total" },
      defaultW: 3,
      defaultH: 2,
    },
    {
      key: "metric.unassigned",
      label: "Total unassigned",
      description: "The count of projects that have no assignee.",
      section: "Charts",
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      // Label matches the derived form — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { measure: "unassigned" },
      defaultW: 3,
      defaultH: 2,
    },
  ],
  render: MetricRender,
};
